<template>
    <div v-if="editMode">
        <ModalComponent
            :is-open="isModalOpen"
            @close="closeModal"
        >
            <button
                @click="closeModal"
                class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
            >
                <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>

            <h3 class="font-bold text-lg mb-4">Separator sectiune</h3>
            <p class="divider-editor__desc">Alege un efect vizual care se afiseaza sub aceasta sectiune.</p>

            <div class="divider-editor__types">
                <button
                    v-for="opt in typeOptions"
                    :key="opt.value"
                    class="divider-editor__type-btn"
                    :class="{ 'divider-editor__type-btn--active': currentType === opt.value }"
                    @click="selectType(opt.value)"
                >
                    <svg
                        v-if="opt.preview"
                        class="divider-editor__preview-svg"
                        viewBox="0 0 200 40"
                        preserveAspectRatio="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path :d="opt.preview" fill="#64748b" />
                    </svg>
                    <div
                        v-else-if="opt.value === 'gradient'"
                        class="divider-editor__preview-gradient"
                    ></div>
                    <div
                        v-else
                        class="divider-editor__preview-none"
                    ></div>
                    <span class="divider-editor__type-label">{{ opt.label }}</span>
                </button>
            </div>

            <div v-if="currentType && currentType !== 'none'" class="divider-editor__options">
                <label class="divider-editor__field">
                    <span class="divider-editor__field-label">Culoare</span>
                    <div class="divider-editor__color-row">
                        <input
                            type="color"
                            :value="currentColor"
                            @input="updateColor($event.target.value)"
                            class="divider-editor__color-input"
                        />
                        <span class="divider-editor__color-hex">{{ currentColor }}</span>
                        <button
                            class="divider-editor__color-auto"
                            @click="autoColor"
                            title="Foloseste culoarea sectiunii urmatoare"
                        >
                            Auto
                        </button>
                    </div>
                </label>

                <label v-if="currentType !== 'gradient'" class="divider-editor__field">
                    <span class="divider-editor__field-label">Inverseaza</span>
                    <input
                        type="checkbox"
                        class="toggle toggle-sm toggle-success"
                        :checked="currentFlip"
                        @change="updateFlip($event.target.checked)"
                    />
                </label>
            </div>
        </ModalComponent>

        <div class="tooltip tooltip-right" data-tip="Separator">
            <button
                class="btn btn-square btn-sm md:btn-md md:text-lg"
                :class="{ 'btn-accent': hasDivider }"
                @click="openModal"
            >
                <font-awesome-icon icon="fa-solid fa-bezier-curve" />
            </button>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ModalComponent from '@/components/ModalComponent.vue';

export default {
    props: {
        section: {
            type: Object,
            required: true,
        },
        sectionKey: {
            type: [String, Number],
            required: true,
        },
        nextSectionBgColor: {
            type: String,
            default: '#ffffff',
        },
    },
    components: {
        ModalComponent,
    },
    data() {
        return {
            isModalOpen: false,
            typeOptions: [
                {
                    value: 'none',
                    label: 'Niciunul',
                    preview: null,
                },
                {
                    value: 'wave',
                    label: 'Val',
                    preview: 'M0,20 C50,40 100,0 150,20 C175,30 190,26 200,20 L200,40 L0,40 Z',
                },
                {
                    value: 'curve',
                    label: 'Curba',
                    preview: 'M0,40 C66,0 133,0 200,40 L200,40 L0,40 Z',
                },
                {
                    value: 'slant',
                    label: 'Oblic',
                    preview: 'M0,40 L200,0 L200,40 Z',
                },
                {
                    value: 'zigzag',
                    label: 'Zigzag',
                    preview: 'M0,40 L25,12 L50,40 L75,12 L100,40 L125,12 L150,40 L175,12 L200,40 Z',
                },
                {
                    value: 'arrow',
                    label: 'Sageata',
                    preview: 'M0,40 L100,0 L200,40 Z',
                },
                {
                    value: 'gradient',
                    label: 'Gradient',
                    preview: null,
                },
            ],
        };
    },
    computed: {
        ...mapGetters(['isEditMode']),
        editMode() {
            return this.isEditMode;
        },
        divider() {
            return this.section.divider || {};
        },
        currentType() {
            return this.divider.type || 'none';
        },
        currentColor() {
            return this.divider.color || '#ffffff';
        },
        currentFlip() {
            return !!this.divider.flip;
        },
        hasDivider() {
            return this.currentType !== 'none';
        },
    },
    methods: {
        openModal() {
            this.isModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
        },
        emitUpdate(dividerData) {
            const updated = { ...this.section, divider: { ...this.divider, ...dividerData } };
            this.$emit('update-section', this.sectionKey, updated);
        },
        selectType(type) {
            if (type === 'none') {
                const updated = { ...this.section };
                delete updated.divider;
                this.$emit('update-section', this.sectionKey, updated);
            } else {
                this.emitUpdate({ type, color: this.currentColor || this.nextSectionBgColor });
            }
        },
        updateColor(color) {
            this.emitUpdate({ color });
        },
        updateFlip(flip) {
            this.emitUpdate({ flip });
        },
        autoColor() {
            this.emitUpdate({ color: this.nextSectionBgColor });
        },
    },
};
</script>

<style scoped>
.divider-editor__desc {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 16px;
    line-height: 1.5;
}

.divider-editor__types {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
    gap: 8px;
    margin-bottom: 20px;
}
.divider-editor__type-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 10px 6px 8px;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
}
.divider-editor__type-btn:hover {
    border-color: #94a3b8;
}
.divider-editor__type-btn--active {
    border-color: #3b82f6;
    background: #eff6ff;
}
.divider-editor__preview-svg {
    width: 100%;
    height: 24px;
    display: block;
}
.divider-editor__preview-gradient {
    width: 100%;
    height: 24px;
    border-radius: 4px;
    background: linear-gradient(to bottom, transparent, #94a3b8);
}
.divider-editor__preview-none {
    width: 100%;
    height: 24px;
    border-radius: 4px;
    border: 1px dashed #cbd5e1;
}
.divider-editor__type-label {
    font-size: 11px;
    font-weight: 600;
    color: #475569;
}

.divider-editor__options {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding-top: 4px;
}
.divider-editor__field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
.divider-editor__field-label {
    font-size: 13px;
    font-weight: 600;
    color: #334155;
}
.divider-editor__color-row {
    display: flex;
    align-items: center;
    gap: 8px;
}
.divider-editor__color-input {
    width: 32px;
    height: 32px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 2px;
    cursor: pointer;
    background: none;
}
.divider-editor__color-hex {
    font-size: 12px;
    font-family: monospace;
    color: #64748b;
}
.divider-editor__color-auto {
    padding: 4px 10px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f8fafc;
    font-size: 11px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    transition: background 0.15s;
}
.divider-editor__color-auto:hover {
    background: #e2e8f0;
}
</style>
